import { memo, useMemo } from 'react'
import { Skeleton } from '../Skeleton'

export interface SelectOption {
  label: string
  value: string | number
  disabled?: boolean
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  options: SelectOption[]
  error?: boolean
  onChange?: (value: string) => void
  containerClassName?: string
  // Shows a pulsing placeholder in the select's shape while its value loads
  skeleton?: boolean
}

/**
 * Select Atom component
 * Following Atomic Design and UI/UX Pro Max guidelines:
 * - Professional Indigo/Slate color palette
 * - Clear focus states
 * - Custom arrow for better aesthetic (using SVG)
 * - Accessibility support (aria-invalid, spacing)
 */
export const Select = memo(
  ({
    options,
    error,
    className,
    onChange,
    value,
    containerClassName,
    disabled,
    skeleton,
    ...rest
  }: SelectProps) => {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e.target.value)
    }

    const selectClasses = useMemo(
      () =>
        [
          'form-control',
          'select-atom',
          error ? 'form-control--error' : '',
          disabled ? 'form-control--disabled' : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' '),
      [error, disabled, className],
    )

    const containerClasses = useMemo(
      () => ['select-atom-container', containerClassName ?? ''].filter(Boolean).join(' '),
      [containerClassName],
    )

    if (skeleton) {
      return <Skeleton shape="input" />
    }

    return (
      <div className={containerClasses}>
        <select
          className={selectClasses}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          aria-invalid={error}
          {...rest}>
          {options.map(option => (
            <option key={String(option.value)} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="select-atom__icon" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-5 h-5"
            role="img"
            aria-label="Chevron down">
            <path
              fillRule="evenodd"
              d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </div>
    )
  },
)

Select.displayName = 'Select'
